import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Award, Download, FileText, ShieldCheck, Clock, BadgeCheck } from "lucide-react";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { apiRequest } from "../../../lib/api";
import { formatDate } from "../../../utils/format";
import { useAuth } from "../../../hooks/useAuth";

interface CoeIssuance {
  issuedAt: string;
  includeSalary: boolean;
  s3Key: string;
}

function useMyCoes() {
  return useQuery({
    queryKey: ["coe", "mine"],
    queryFn: () => apiRequest<{ items: CoeIssuance[] }>("/me/coe").then((r) => r.items),
    staleTime: 0,
  });
}

export default function CoePage() {
  const { toasts, toast, dismissToast } = useToast();
  const [includeSalary, setIncludeSalary] = useState(false);
  const [generating, setGenerating] = useState(false);
  const { data: history, refetch } = useMyCoes();
  const { activeBusinessId } = useAuth();

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await apiRequest<{ url: string; issuedAt: string }>("/me/coe", {
        method: "POST",
        body: { includeSalary, ...(activeBusinessId ? { businessId: activeBusinessId } : {}) },
      });
      window.open(result.url, "_blank", "noopener,noreferrer");
      toast.success("Certificate generated. Opening in new tab…");
      await refetch();
    } catch {
      toast.error("Failed to generate certificate. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* ── Hero action card ──────────────────────────────────────────────── */}
      <div className="relative rounded-2xl bg-gradient-to-br from-[#2447d9] to-[#1a33a8] p-6 shadow-lg overflow-hidden">
        {/* Decorative circles */}
        <div className="pointer-events-none absolute -top-10 -right-10 h-48 w-48 rounded-full bg-white/5" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 h-36 w-36 rounded-full bg-white/5" />

        {/* Title */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
            <Award className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white leading-tight">Certificate of Employment</h1>
            <p className="text-xs text-white/60">Official employment certificate · opens as PDF</p>
          </div>
        </div>

        {/* Salary toggle — the only config decision, given clear visual weight */}
        <div
          className="mb-5 flex items-center justify-between rounded-xl bg-white/10 px-4 py-3.5 cursor-pointer hover:bg-white/15 transition-colors"
          onClick={() => setIncludeSalary((v) => !v)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === " " && setIncludeSalary((v) => !v)}
        >
          <div>
            <p className="text-sm font-medium text-white">Include monthly salary</p>
            <p className="text-xs text-white/50 mt-0.5">Your basic salary will appear on the certificate</p>
          </div>
          {/* Toggle */}
          <div
            className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${
              includeSalary ? "bg-white" : "bg-white/20"
            }`}
          >
            <span
              className={`absolute top-1 h-4 w-4 rounded-full shadow transition-transform ${
                includeSalary ? "translate-x-5 bg-[#2447d9]" : "translate-x-1 bg-white/60"
              }`}
            />
          </div>
        </div>

        {/* Generate button */}
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-[#2447d9] hover:bg-white/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          <Download className="h-4 w-4" />
          {generating ? "Generating…" : "Generate & Download COE"}
        </button>

        {/* What's included — compact chip strip */}
        <div className="mt-4 flex flex-wrap gap-2">
          {[
            { icon: <FileText className="h-3 w-3" />, label: "Employee details" },
            { icon: <Clock className="h-3 w-3" />,     label: "Tenure & hire date" },
            { icon: <ShieldCheck className="h-3 w-3" />, label: "Audit logged" },
          ].map((chip) => (
            <span key={chip.label} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs text-white/70">
              {chip.icon}
              {chip.label}
            </span>
          ))}
        </div>
      </div>

      {/* ── Issuance history ──────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-widest">
            Issuance History
          </p>
          {history && history.length > 0 && (
            <p className="text-xs text-[#94a3b8]">{history.length} certificate{history.length !== 1 ? "s" : ""} generated</p>
          )}
        </div>

        {!history || history.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#e2e8f0] bg-white py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#eff3ff]">
              <BadgeCheck className="h-6 w-6 text-[#2447d9]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#0f172a]">No certificates yet</p>
              <p className="text-xs text-[#94a3b8] mt-0.5">Your first COE will appear here after generation.</p>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-[#e2e8f0] bg-white overflow-hidden shadow-sm">
            {history.map((coe, i) => (
              <div
                key={coe.issuedAt}
                className={`flex items-center justify-between px-5 py-3.5 ${
                  i !== history.length - 1 ? "border-b border-[#f1f5f9]" : ""
                } hover:bg-[#f8fafc] transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#eff3ff]">
                    <FileText className="h-3.5 w-3.5 text-[#2447d9]" />
                  </div>
                  <span className="text-sm text-[#0f172a]">{formatDate(coe.issuedAt)}</span>
                </div>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  coe.includeSalary
                    ? "bg-[#eff3ff] text-[#2447d9]"
                    : "bg-[#f1f5f9] text-[#64748b]"
                }`}>
                  {coe.includeSalary ? "With salary" : "Without salary"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
